import { useRef, useState, type ClipboardEvent, type KeyboardEvent, type RefObject } from 'react';
import { fenceCode, insideFence, looksLikeCode } from './richtext';

interface Pasted {
  plain: string;
  wrapped: string;
  at: number;
  /** The browser keeps the wrapping as a step of its own (Ctrl+Z undoes it). */
  native: boolean;
}

/**
 * Pasting code into a message box wraps it in a fence (```), so it shows as code. The paste goes
 * in as usual, and the fence is a second step: undo once (Ctrl+Z, ⌘Z, or Undo in the note under
 * the box, which phones need) and it's the plain text that was pasted. Prose is left alone
 * (looksLikeCode is careful), and so is a paste inside a block that's already code.
 */
export function usePasteAsCode(opts: {
  area: RefObject<HTMLTextAreaElement | null>;
  /** The box's own limit: a paste that would go over it once fenced stays plain. */
  limit: number;
  on: boolean;
  /** Puts this in the box, with the caret here (when the browser can't make the change itself). */
  setText: (value: string, caret: number) => void;
}) {
  const [note, setNote] = useState(false);
  const pasted = useRef<Pasted | null>(null);
  /** The change being made is the fence itself, not theirs. */
  const ours = useRef(false);

  const forget = () => {
    if (pasted.current) pasted.current = null;
    setNote(false);
  };

  /** The box's onPaste. */
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!opts.on) return;
    const raw = e.clipboardData.getData('text/plain');
    if (!raw || !looksLikeCode(raw)) return;
    const el = e.currentTarget;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    if (insideFence(el.value, start)) return;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    const plain = raw.replace(/\r\n?/g, '\n');
    const wrapped = fenceCode(plain, before, after);
    if (before.length + wrapped.length + after.length > opts.limit) return;
    window.setTimeout(() => {
      // The paste went in as it was (not cut short at the box's limit, say): now the fence.
      if (el.value !== before + plain + after) return;
      el.focus();
      el.setSelectionRange(start, start + plain.length);
      let native = false;
      ours.current = true;
      try {
        native = document.execCommand('insertText', false, wrapped);
      } catch {
        native = false;
      }
      ours.current = false;
      if (!native || el.value !== before + wrapped + after) {
        native = false;
        opts.setText(before + wrapped + after, before.length + wrapped.length);
      }
      pasted.current = { plain, wrapped, at: start, native };
      setNote(true);
    }, 0);
  };

  /** The box's onChange: their own change (typing, the browser's undo) ends the offer to undo. */
  const changed = () => {
    if (!ours.current && pasted.current) forget();
  };

  /** Back to the plain text that was pasted. */
  const undo = () => {
    const p = pasted.current;
    const el = opts.area.current;
    forget();
    if (!p || !el) return;
    const value = el.value;
    const at = value.startsWith(p.wrapped, p.at) ? p.at : value.indexOf(p.wrapped);
    if (at < 0) return;
    const next = value.slice(0, at) + p.plain + value.slice(at + p.wrapped.length);
    el.focus();
    el.setSelectionRange(at, at + p.wrapped.length);
    let ok = false;
    ours.current = true;
    try {
      ok = document.execCommand('insertText', false, p.plain);
    } catch {
      ok = false;
    }
    ours.current = false;
    if (!ok || el.value !== next) opts.setText(next, at + p.plain.length);
  };

  /**
   * The box's onKeyDown, first: Ctrl+Z (⌘Z) right after the paste, when the browser keeps no step
   * of its own for the fence. True when it was handled.
   */
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    const p = pasted.current;
    if (!p || p.native || !(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== 'z') return false;
    e.preventDefault();
    undo();
    return true;
  };

  return { onPaste, changed, undo, onKey, note, forget };
}
